<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import DirectoryItem from '@/components/files/DirectoryItem.vue';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';
import { useModuleStore } from '@/stores/module';
import type { FileContent } from '@/types/file';
import type { Module } from '@/types/module';
import { getDirectoryContent } from '@/utils/file';

const currentModule = ref<Module | null>();
const moduleStore = useModuleStore();
const authStore = useAuthStore();
const globalStore = useGlobalStore();

const route = useRoute();

const directoryContent = ref<FileContent[]>([]);
const isInitializing = ref<boolean>(true);

onMounted(async () => {
    if (!route.params.module) return;

    currentModule.value = await moduleStore.fetchByName(route.params.module.toString());

    if (currentModule.value == null) {
        isInitializing.value = false;
        return;
    }

    directoryContent.value = await getDirectoryContent(authStore.getCurrentOrganization()._id, currentModule.value?.name.toString());
    isInitializing.value = false;
});

watch(
    () => route.params.module,
    async (newValue, oldValue) => {
        if (newValue != oldValue && newValue) {
            currentModule.value = await moduleStore.fetchByName(newValue.toString());
        }
    }
);
const searchInput = ref('');

const filteredContent = computed(() => {
    if (!searchInput.value.trim()) return directoryContent.value;

    return searchInDirectory(directoryContent.value, searchInput.value);
});

function searchInDirectory(directory: FileContent[], query: string): FileContent[] {
    const results: FileContent[] = [];
    for (const item of directory) {
        if (item.type === 'directory') {
            if (item.name.toLowerCase().includes(query.toLowerCase())) {
                results.push(item);
            }
            if (item.children) {
                results.push(...searchInDirectory(item.children, query));
            }
        } else if (item.name.toLowerCase().includes(query.toLowerCase())) {
            results.push(item);
        }
    }
    return results;
}
</script>

<template>
    <v-fade-transition>
        <div v-show="!isInitializing && !globalStore.isLoading" class="mb-5">
            <template v-if="currentModule">
                <h1 class="text-main">{{ currentModule.name }} | <span class="text-alt text-h5"> documenten </span></h1>
                <v-row v-if="directoryContent.length > 0">
                    <v-col cols="12" lg="4">
                        <v-text-field v-model="searchInput" label="Zoeken" placeholder="Typ om te zoeken..." />
                    </v-col>
                </v-row>

                <div class="document-list shadow-sm" v-if="filteredContent.length > 0">
                    <ul class="pa-0">
                        <DirectoryItem
                            v-for="(fileOrDirectory, index) in filteredContent"
                            :key="index"
                            :fileOrDirectory="fileOrDirectory"
                            :organizationId="authStore.getCurrentOrganization()._id"
                        />
                    </ul>
                </div>
                <div v-else>
                    <p>Er zijn geen documenten gevonden.</p>
                </div>
            </template>
            <template v-else>
                <p>Deze module bestaat niet.</p>
            </template>
        </div>
    </v-fade-transition>
</template>

<style scoped>
.document-list {
    background-color: var(--c-white);
}

.document-list ul {
    list-style-type: none;
}
</style>
